import moment from 'moment'
import i18n from '@/locales'
import { currencyUnitMap } from '@/constants/currency'
import SystemIcon from '@/sections/SystemIcon'
import { numerify } from '@/filters'

export const getAccountingPeriodColumn = () => {
  return {
    title: i18n.t('bill.accounting_period'),
    sortable: true,
    field: 'month',
    formatter: ({ row }) => {
      return row.month || '-'
    },
  }
}

export const getContractNumberColumn = () => {
  return {
    title: i18n.t('bill.contract_number'),
    field: 'offering_class',
    formatter: ({ row }) => {
      return row.offering_class || '-'
    },
  }
}

export const getServicePartNumberColumn = () => {
  return {
    title: i18n.t('bill.service_part_number'),
    field: 'payment_option',
    formatter: ({ row }) => {
      return row.payment_option || '-'
    },
  }
}

export const getServiceDescriptionColumn = ({ edit = false, onManager }) => {
  return {
    title: i18n.t('bill.service_description'),
    field: 'category',
    sortable: true,
    slots: {
      default: ({ row }, h) => {
        return [
          h('list-body-cell-wrap', {
            props: {
              copy: true,
              field: 'category',
              label: i18n.t('bill.service_description'),
              edit: edit,
              formRules: [{ required: true, message: i18n.t('common.tips.input', [i18n.t('bill.service_description')]) }],
              onManager: onManager,
              row,
              titleClass: 'text-wrap',
            },
          }),
        ]
      },
    },
  }
}

export const getRegionColumn = () => {
  return {
    title: i18n.t('bill.text_83'),
    field: 'cpp_region',
    formatter: ({ row }) => {
      return row.cpp_region || '-'
    },
  }
}

export const getModelColumn = () => {
  return {
    title: i18n.t('bill.model'),
    field: 'os_sku',
    formatter: ({ row }) => {
      return row.os_sku || '-'
    },
  }
}

export const getPromiseMonthColumn = () => {
  return {
    title: i18n.t('bill.promise_month'),
    field: 'reservation_months',
    formatter: ({ row }) => {
      return row.reservation_months || '-'
    },
  }
}

export const getCppCountColumn = () => {
  return {
    title: i18n.t('bill.count'),
    field: 'cpp_count',
    formatter: ({ row }) => {
      return row.cpp_count || '-'
    },
  }
}

export const getUnitPriceColumn = () => {
  return {
    title: i18n.t('bill.unit_price'),
    field: 'cpp_rate',
    formatter: ({ row }) => {
      const { currency = 'CNY', cpp_rate = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(cpp_rate, '0,0.00')}`
    },
  }
}

export const getVatColumn = () => {
  return {
    title: i18n.t('bill.vat'),
    field: 'vat',
    formatter: ({ row }) => {
      const { currency = 'CNY', vat = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(vat, '0,0.00')}`
    },
  }
}

export const getAmountPriceColumn = () => {
  return {
    title: i18n.t('bill.amount_price'),
    field: 'monthly_cost',
    formatter: ({ row }) => {
      const { currency = 'CNY', monthly_cost = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(monthly_cost, '0,0.00')}`
    },
  }
}

export const getEffectiveMonthColumn = () => {
  return {
    title: i18n.t('bill.effective_month'),
    field: 'usage_start_time',
    formatter: ({ row }) => {
      if (!row.usage_start_time) return '-'
      return moment(row.usage_start_time).format('YYYY-MM')
    },
  }
}

export const getExpirationMonthColumn = () => {
  return {
    title: i18n.t('bill.expiration_month'),
    field: 'usage_end_time',
    formatter: ({ row }) => {
      if (!row.usage_end_time) return '-'
      return moment(row.usage_end_time).format('YYYY-MM')
    },
  }
}

export const getAmountHoursColumn = () => {
  return {
    title: i18n.t('bill.amount_hours'),
    field: 'cpp_total_hours',
    formatter: ({ row }) => {
      return numerify(row.cpp_total_hours, '0.00') + ' h'
    },
  }
}

export const getUsedHoursColumn = () => {
  return {
    title: i18n.t('bill.used_hours'),
    field: 'cpp_duration',
    slots: {
      default: ({ row }, h) => {
        if (!row.total_used_duration || !row.cpp_total_hours) return `${numerify(row.total_used_duration, '0.00')} h`
        const percent = numerify(((row.total_used_duration / row.cpp_total_hours) * 100), '0.00')
        const percentStyle = {}
        if (percent > 100) {
          percentStyle.color = 'red'
        } else if (percent < 50) {
          percentStyle.color = 'orange'
        }
        return [<span>{`${numerify(row.total_used_duration, '0.00')} h`}<span style={percentStyle}>{ ` (${percent}%)` }</span></span>]
      },
    },
  }
}

export const getCppCostColumn = () => {
  return {
    title: i18n.t('bill.cpp_cost'),
    field: 'cpp_cost',
    formatter: ({ row }) => {
      const { currency = 'CNY', cpp_cost = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(cpp_cost, '0,0.00')}`
    },
  }
}

export const getOnDemandCostColumn = () => {
  return {
    title: i18n.t('bill.on_demand_cost'),
    field: 'postpaid_cost',
    formatter: ({ row }) => {
      const { currency = 'CNY', postpaid_cost = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(postpaid_cost, '0,0.00')}`
    },
  }
}

export const getAmountColumn = () => {
  return {
    title: i18n.t('bill.amount'),
    field: 'cpp_total_cost',
    formatter: ({ row }) => {
      const { currency = 'CNY', cpp_total_cost = 0 } = row
      const sign = currencyUnitMap[currency].sign
      return `${sign} ${numerify(cpp_total_cost, '0,0.00')}`
    },
  }
}

export const getImportTimeColumn = () => {
  return {
    title: i18n.t('bill.import_time'),
    field: 'created_at',
    formatter: ({ row }) => {
      if (!row.created_at) return '-'
      return moment(row.created_at).format('YYYY-MM-DD HH:mm:ss')
    },
  }
}

export const getOsTypeTableColumn = () => {
  return {
    field: 'os_type',
    title: i18n.t('table.title.os'),
    width: 50,
    slots: {
      default: ({ row }, h) => {
        if (!row.os_type) return
        let name = row.os_type
        let tooltip = ''

        if (row.os_type) {
          tooltip = row.os_type
        } else {
          tooltip = i18n.t('compute.text_339')
        }

        if (name.includes('Windows') || name.includes('windows')) {
          name = 'Windows'
        } else if (name.includes('Linux') || name.includes('linux')) {
          name = 'Linux'
        }
        return [
          <SystemIcon tooltip={tooltip} name={name} />,
        ]
      },
    },
  }
}
